import { useState } from 'react';

interface FinalCTAProps {
  onOpenConsultation: () => void;
}

export default function FinalCTA({ onOpenConsultation }: FinalCTAProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');

  const handleQuickSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim() || !name.trim()) return;
    setLoading(true);
    try {
      const body = new URLSearchParams({ name, phone, source: 'Final CTA Form' });
      await fetch('https://readdy.ai/api/form/d6va47u7ipaugi04lhug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="py-28 relative overflow-hidden"
      style={{ background: 'linear-gradient(145deg, #0c2236 0%, #0d3528 45%, #152e44 80%, #0c2236 100%)' }}
    >
      {/* Decorative elements */}
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full border border-white/[0.04] pointer-events-none" />
      <div className="absolute -bottom-40 -left-24 w-96 h-96 rounded-full border border-white/[0.04] pointer-events-none" />
      <div
        className="absolute top-0 right-0 w-full h-full pointer-events-none opacity-30"
        style={{ backgroundImage: 'radial-gradient(ellipse at 80% 50%, rgba(200,169,106,0.08) 0%, transparent 60%)' }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: messaging + CTAs */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-block w-10 h-px bg-gold opacity-70"></span>
              <p className="text-gold text-xs font-bold uppercase tracking-[0.2em]">Take the First Step</p>
            </div>
            <h2 className="font-heading text-white font-bold mb-6 leading-[1.05]" style={{ fontSize: 'clamp(2rem, 3.5vw, 3rem)' }}>
              Get Trusted Care for Your<br />
              <em className="text-gold not-italic">Loved One Today</em>
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-9 max-w-lg">
              Our compassionate care team is ready to help. Schedule a free consultation and we&apos;ll create a personalized care plan — often within 24 hours.
            </p>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-4 mb-9">
              <a
                href="tel:2173462042"
                className="whitespace-nowrap flex items-center gap-2.5 bg-gold text-navy font-bold px-9 py-4 rounded-full hover:brightness-110 transition-all text-sm group"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-fill text-base"></i>
                </span>
                Call: (217) 346-2042
              </a>
              <button
                onClick={onOpenConsultation}
                className="whitespace-nowrap flex items-center gap-2.5 bg-white/10 border border-white/20 text-white font-bold px-9 py-4 rounded-full hover:bg-white/18 transition-all text-sm cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-calendar-line text-base"></i>
                </span>
                Schedule Consultation
              </button>
            </div>

            {/* Trust signals */}
            <div className="flex flex-wrap gap-5 text-white/40 text-xs pt-5 border-t border-white/10">
              <span className="flex items-center gap-2">
                <i className="ri-time-line text-sage text-sm"></i>
                Response within 2 hours
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-shield-check-line text-sage text-sm"></i>
                Free, no-obligation
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-star-fill text-gold text-sm"></i>
                5-star rated care
              </span>
            </div>
          </div>

          {/* Right: Inline quick form */}
          <div
            className="bg-white rounded-2xl p-9"
            style={{ boxShadow: '0 8px 60px rgba(15,42,68,0.5)' }}
          >
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center bg-sage/20 rounded-full mx-auto mb-5">
                  <i className="ri-check-line text-3xl text-sage"></i>
                </div>
                <h3 className="font-heading text-navy text-2xl font-bold mb-3">We&apos;ll Call You Soon!</h3>
                <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">
                  Thank you! Our care team will reach out within 2 hours to discuss your loved one&apos;s needs.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-7">
                  <p className="text-gold text-xs font-bold uppercase tracking-[0.18em] mb-1.5">Free Consultation Request</p>
                  <h3 className="font-heading text-navy text-2xl font-bold leading-tight">Get a Callback Today</h3>
                  <p className="text-gray-400 text-sm mt-1.5">Our care coordinators respond within 2 hours.</p>
                </div>
                <form data-readdy-form onSubmit={handleQuickSubmit} className="space-y-4">
                  <div>
                    <label className="text-navy text-xs font-bold uppercase tracking-wider block mb-2">Your Name</label>
                    <input
                      type="text"
                      name="name"
                      placeholder="Full name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="w-full border border-gray-200 bg-offWhite rounded-xl px-5 py-3.5 text-sm text-navy placeholder-gray-300 focus:outline-none focus:border-sage focus:bg-white transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-navy text-xs font-bold uppercase tracking-wider block mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="(xxx) xxx-xxxx"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      className="w-full border border-gray-200 bg-offWhite rounded-xl px-5 py-3.5 text-sm text-navy placeholder-gray-300 focus:outline-none focus:border-sage focus:bg-white transition-all"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!name.trim() || !phone.trim() || loading}
                    className="whitespace-nowrap w-full bg-gold text-navy font-bold py-4 rounded-xl hover:brightness-105 transition-all cursor-pointer disabled:opacity-40 text-sm"
                  >
                    {loading ? 'Sending...' : 'Request Free Callback →'}
                  </button>
                </form>
                <p className="text-gray-300 text-xs text-center mt-5">
                  No obligation &nbsp;·&nbsp; No pressure &nbsp;·&nbsp; 100% confidential
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
